import React from 'react';

import { Flex } from '@chakra-ui/react';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { cloneDeep } from 'lodash';
import { useDispatch } from 'react-redux';
import {
  setReviewCreationGridDefinition,
  useGridDefinition,
  useSelectReviewCreationGridMetaData,
} from 'reducers/reviewCreationV1Slice';
import { generateRows } from 'screens/admin/Reviews/CalibrationSettings/common/helper';
import { EditScoreComp } from 'screens/admin/Reviews/ReviewProcessV1/Steps/Calibration/EditScoreComp';

import { StringModifier, modifyString } from 'constants/copyHelpers';

import { CalibrationGrid } from 'uiToolkitV1/CalibrationGrid';
import { SelectDashed } from 'uiToolkitV1/Select';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextLabel, TextLabelSmall } from 'uiToolkitV1/Typography';

const GridDimensionOptions = [
  { label: '1x3', value: '1x3', data: { rows: 1, columns: 3, boxCount: 3 } },
  { label: '2x3', value: '2x3', data: { rows: 2, columns: 3, boxCount: 6 } },
  { label: '3x3', value: '3x3', data: { rows: 3, columns: 3, boxCount: 9 } },
  { label: '4x4', value: '4x4', data: { rows: 4, columns: 4, boxCount: 16 } },
];

const BOX_OPTIONS = [3, 6, 9, 16];

export const PARAM_KEYS = {
  LABEL: 'label',
  COLOR: 'color',
  SCALE: 'SCALE',
};
const BOX_OPTIONS_MAP = {
  3: '1x3',
  6: '2x3',
  9: '3x3',
  16: '4x4',
};

const GridAxisOptions = [
  { label: 'Performance', value: 'Performance' },
  { label: 'Potential', value: 'Potential' },
  { label: 'Overall', value: 'Overall' },
];

const getMaxScore = ({ grid }) => {
  // top right grid time will have max score for both x and y axis.
  // that is last item of last row of grid.
  // also max score in both y and x axis is always same
  // grid is and object with keys 1, 2, 3...
  // and for each key we have array of objects for column wise cell data

  const maxObjectKeyOfGrid = Math.max(...Object.keys(grid));
  const gridLastRowDataArray = grid[maxObjectKeyOfGrid];
  // top right tile on UI
  const gridLastTileData = gridLastRowDataArray[gridLastRowDataArray.length - 1];

  // any of x or y axisData
  const keys = Object.keys(gridLastTileData.scoreRange);
  const { maxScore } = gridLastTileData.scoreRange[keys[0]];

  return maxScore;
};

const GridOptionsHeader = () => {
  const { boxCount, rowsCount, columnsCount } = useSelectReviewCreationGridMetaData();
  const gridDefinition = useGridDefinition();
  const dispatch = useDispatch();
  const { objectivesName } = useOrganisationSettings();

  const onGridChangeHandler = ({ value, data }) => {
    const { rows, columns, boxCount } = data;
    const maxScore = getMaxScore({ grid: gridDefinition });
    const newGrid = generateRows({ rows, cols: columns, maxScore });
    dispatch(setReviewCreationGridDefinition(newGrid));

    console.log(value);
  };

  return (
    <Flex alignItems="center" justifyContent="stretch" gap={3}>
      <Flex flexDir="column" flexGrow={1} gap={3}>
        <TextLabel color="text.g2">Grid Type</TextLabel>
        <SelectDashed
          options={GridDimensionOptions}
          value={GridDimensionOptions.filter((option) => option.value === BOX_OPTIONS_MAP[boxCount])[0]}
          handleChange={onGridChangeHandler}
        />
      </Flex>

      {boxCount !== BOX_OPTIONS[0] && (
        <Flex flexDir="column" flexGrow={1} gap={3}>
          <Flex alignItems="center">
            <TextLabel mr={1} color="text.g2">
              Vertical Axis Name
            </TextLabel>
            <TooltipInfoV1 title="Potential score is based on the Overall Competency Score" />
          </Flex>
          <SelectDashed options={GridAxisOptions} isDisabled value={GridAxisOptions[1]} />
        </Flex>
      )}

      <Flex flexDir="column" flexGrow={1} gap={3}>
        <Flex alignItems="center">
          <TextLabel mr={1} color="text.g2">
            Horizontal Axis Name
          </TextLabel>
          <TooltipInfoV1
            title={`Performance score is based on the Overall ${modifyString(
              objectivesName,
              StringModifier.PLURAL_TITLE_CASE
            )} Score`}
          />
        </Flex>
        <SelectDashed
          options={GridAxisOptions}
          value={boxCount === BOX_OPTIONS[0] ? GridAxisOptions[2] : GridAxisOptions[0]}
          isDisabled
        />
      </Flex>
    </Flex>
  );
};

const gridCompTranslator = (gridDefinition, rowsCount, columnsCount) => {
  const gridArray = Array.from({ length: rowsCount }).map((_, i) => {
    const rowKey = i + 1;
    return Array.from({ length: columnsCount }).map((_, j) => ({
      color: gridDefinition[rowKey][j].color,
      label: gridDefinition[rowKey][j].label,
    }));
  });

  // we need to reverse  because of difference b/w data
  // structure of grid is vertically reverse of what we want visually on the UI
  gridArray.reverse();
  return gridArray;
};

const VerticalScale = ({ verticalScale, maxScore }) => {
  const gridDefinition = useGridDefinition();
  return (
    <Flex flexDir="column-reverse" justifyContent="space-between" pr={3} pb={7}>
      {verticalScale.map((val, idx) => (
        <Flex alignItems="center">
          <TextLabelSmall bold>{val}</TextLabelSmall>
          {idx !== 0 && idx !== verticalScale.length - 1 && (
            <EditScoreComp
              initialScore={val}
              maxScore={maxScore}
              direction="POTENTIAL"
              gridDefinition={gridDefinition}
              isSimplifiedReviewCreation
            />
          )}
        </Flex>
      ))}
    </Flex>
  );
};

const HorizontalScale = ({ horizontalScale, maxScore }) => {
  const { boxCount, rowsCount, columnsCount } = useSelectReviewCreationGridMetaData();
  const gridDefinition = useGridDefinition();

  return (
    <Flex justifyContent="space-between" pt={3}>
      {horizontalScale.map((val, idx) => (
        <Flex alignItems="center">
          <TextLabelSmall bold>{val}</TextLabelSmall>
          {idx !== 0 && idx !== horizontalScale.length - 1 && (
            <EditScoreComp
              initialScore={val}
              maxScore={maxScore}
              direction={rowsCount === 1 ? 'OVERALL' : 'PERFORMANCE'}
              gridDefinition={gridDefinition}
              isSimplifiedReviewCreation
            />
          )}
        </Flex>
      ))}
    </Flex>
  );
};

const GridSection = () => {
  const gridDefinition = useGridDefinition();
  const dispatch = useDispatch();
  const { rowsCount, columnsCount } = useSelectReviewCreationGridMetaData();

  const maxScore = getMaxScore({ grid: gridDefinition });

  const calculateVerticalScale = () => {
    const vertScale = [];
    const columnLength = Object.keys(gridDefinition).length;
    if (columnLength <= 1) return vertScale;
    Object.keys(gridDefinition).forEach((key) =>
      gridDefinition[key].map((item) => {
        const keys = Object.keys(item.scoreRange);
        vertScale.push(item.scoreRange[keys[1]].maxScore);
      })
    );
    const vertScaleArray = new Set(vertScale);
    return [0, ...vertScaleArray];
  };

  const calculateHorizontalScale = () => {
    // horizontal scale will be in any case.
    const horizScale = gridDefinition[1].map((item) => {
      const keys = Object.keys(item.scoreRange);
      return item.scoreRange[keys[0]].maxScore;
    });
    return [0, ...horizScale];
  };

  const verticalScale = calculateVerticalScale();
  const horizontalScale = calculateHorizontalScale();

  const onGridDataChangeHandler = (rowIndex, colIndex, key, value) => {
    const newGrid = cloneDeep(gridDefinition);

    // this is required because: gridDefinition is object with 1 index keys, where as the visual grid component
    // is 0 index array of array.
    // also order of rows is reverse b/w grid definition and the grid component.
    // so we need to calculate object row key to make the modification
    const rowObjectKey = rowsCount - rowIndex;

    newGrid[rowObjectKey][colIndex] = { ...newGrid[rowObjectKey][colIndex], [key]: value };

    dispatch(setReviewCreationGridDefinition(newGrid));
  };

  // takes care of inverting grid vertically for the purpose of display
  const gridDefinitionCalc = gridCompTranslator(gridDefinition, rowsCount, columnsCount);

  return (
    <Flex>
      {rowsCount > 1 && <VerticalScale verticalScale={verticalScale} maxScore={maxScore} />}
      <Flex flexDir="column" flexGrow={1}>
        <CalibrationGrid gridDefinition={gridDefinitionCalc} isEditable onChange={onGridDataChangeHandler} />
        <HorizontalScale horizontalScale={horizontalScale} maxScore={maxScore} />
      </Flex>
    </Flex>
  );
};

export { GridOptionsHeader, GridSection };
